import type { GemstoneSelection } from '../types/certificate';

export class CommentLineGenerator {
  
  /**
   * Generate comment lines from gemstone selections
   */
  static generateCommentLines(selections: GemstoneSelection[]): string[] {
    // Step 1: Sort selections (Diamonds first, then others)
    const sortedSelections = this.sortGemstoneSelections(selections);
    
    // Step 2: Generate comment lines with sequential numbering
    const commentLines: string[] = [];
    
    let gemstoneCount = 1;
    for (const selection of sortedSelections.slice(0, 3)) { // Max 3 comment lines
      let commentLine = '';
      
      if (selection.gemstone_type.startsWith('DIAMOND_')) {
        commentLine = this.generateDiamondCommentLine(selection, gemstoneCount);
      } else if (selection.gemstone_type.includes('PEARL')) {
        commentLine = this.generatePearlCommentLine(selection, gemstoneCount);
      } else {
        commentLine = this.generateRegularGemstoneCommentLine(selection, gemstoneCount);
      }
      
      gemstoneCount++;
      
      // Step 3: Apply abbreviations if needed
      commentLine = this.applyAbbreviationsIfNeeded(commentLine);
      
      commentLines.push(commentLine);
    }
    
    return commentLines;
  }
  
  /**
   * Sort gemstone selections (Diamonds first)
   */
  private static sortGemstoneSelections(selections: GemstoneSelection[]): GemstoneSelection[] {
    return selections.sort((a, b) => {
      const aIsDiamond = a.gemstone_type.startsWith('DIAMOND_');
      const bIsDiamond = b.gemstone_type.startsWith('DIAMOND_');
      
      if (aIsDiamond && !bIsDiamond) return -1;
      if (!aIsDiamond && bIsDiamond) return 1;
      
      // If both are diamonds, sort by diamond number (DIAMOND_1, DIAMOND_2, DIAMOND_3)
      if (aIsDiamond && bIsDiamond) {
        const aNum = parseInt(a.gemstone_type.split('_')[1]);
        const bNum = parseInt(b.gemstone_type.split('_')[1]);
        return aNum - bNum;
      }
      
      return 0;
    });
  }
  
  /**
   * Generate diamond comment line with numbered format
   */
  private static generateDiamondCommentLine(selection: GemstoneSelection, count: number): string {
    const prefix = `${count}. DIAMOND`;
    const weight = `${selection.carat_weight || selection.measurement_value} CARATS`;
    const color = selection.diamond_color_range || selection.diamond_color_fixed ? 
      `COLOR: ${selection.diamond_color_range || selection.diamond_color_fixed}` : '';
    const clarity = selection.diamond_clarity_range || selection.diamond_clarity_fixed ? 
      `CLARITY: ${selection.diamond_clarity_range || selection.diamond_clarity_fixed}` : '';
    const origin = selection.growth_origin === 'LAB GROWN' ? 'LAB GROWN' : 'NATURAL';
    
    const parts = [prefix, weight, color, clarity, origin].filter(Boolean);
    return parts.join(', ');
  }
  
  /**
   * Generate pearl comment line with numbered format (no growth origin)
   */
  private static generatePearlCommentLine(selection: GemstoneSelection, count: number): string {
    const type = selection.gemstone_type.replace('_', ' ');
    const prefix = `${count}. ${type}`;
    
    if (selection.measurement_range) {
      return `${prefix}: ${selection.measurement_range} MM`;
    } else if (selection.measurement_value) {
      return `${prefix}: ${selection.measurement_value} MM`;
    } else if (selection.carat_weight) {
      return `${prefix}: ${selection.carat_weight} MM`;
    }
    
    return `${prefix}: SIZE TBD`;
  }
  
  /**
   * Generate regular gemstone comment line with numbered format
   */
  private static generateRegularGemstoneCommentLine(selection: GemstoneSelection, count: number): string {
    const type = selection.gemstone_type.replace('_', ' ');
    const prefix = `${count}. ${type}`;
    const weight = `${selection.carat_weight || selection.measurement_value} CARATS`;
    const origin = selection.growth_origin === 'LAB GROWN' ? 'LAB GROWN' : 'NATURAL';
    
    return `${prefix}: ${weight}, ${origin}`;
  }
  
  /**
   * Apply abbreviations only if character limit exceeded
   */
  private static applyAbbreviationsIfNeeded(commentLine: string): string {
    if (commentLine.length <= 65) {
      return commentLine; // No abbreviations needed
    }
    
    // Apply abbreviations progressively
    let abbreviated = commentLine
      .replace(/CARATS/g, 'CT*')
      .replace(/COLOR:/g, 'COL*:')
      .replace(/CLARITY:/g, 'CL*:')
      .replace(/NATURAL/g, 'NAT*')
      .replace(/LAB GROWN/g, 'LAB*');
    
    return abbreviated.substring(0, 65); // Ensure limit compliance
  }
  
  /**
   * Detect which abbreviations are used in comment lines
   */
  static detectUsedAbbreviations(commentLines: string[]): Set<string> {
    const used = new Set<string>();
    const allText = commentLines.join(' ');
    
    if (allText.includes('CT*')) used.add('CT*');
    if (allText.includes('COL*')) used.add('COL*');
    if (allText.includes('CL*')) used.add('CL*');
    if (allText.includes('NAT*')) used.add('NAT*');
    if (allText.includes('LAB*')) used.add('LAB*');
    
    return used;
  }
} 